import { Button } from '@bambi/component-library/src/components/Button/Button';
import { ContextFormFieldNumber } from '@bambi/component-library/src/components/forms/context/ContextFormFieldNumber';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { AfterHoursPricingRow } from './AfterHoursPricingRow';
import { assignConfigurationDefaultValues } from './assignConfigurationDefaultValues';
import { LateCancellationAmountField } from './late-cancellation/LateCancellationAmountField';
import { LateCancellationFeeTypeField } from './late-cancellation/LateCancellationFeeTypeField';
import type {
  PricingSchemeWriteKeysForStepOne,
  SchemeMetaFormValues,
} from './metaDefaultValues';
import { MileageRoundingSelectField } from './MileageRoundingSelectField';
import { NoShowAmountField } from './no-show/NoShowAmountField';
import { NoShowFeeTypeField } from './no-show/NoShowFeeTypeField';
import { ObservedHolidaysField } from './ObservedHolidaysField';
import { PriceIntervalSelectField } from './PriceIntervalSelectField';
import { PriceRoundingSelectField } from './PriceRoundingSelectField';
import type { PricingWizardMode } from './PricingWizard';
import { StairFeeTypeField } from './StairFeeTypeField';
import { usePricingWizardFormSubmit } from './usePricingWizardFormSubmit';
import type {
  PricingSchemeRead,
  PricingSchemeWrite,
} from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { CurrencyFormContextField } from 'src/common/FormField/CurrencyFormContextField';
import { FormSubmitButton } from 'src/common/FormSubmitButton';
import { useScrollToFormErrors } from 'src/common/useScrollToFormErrors';

export type SchemeConfigFormValues = Omit<
  PricingSchemeWrite,
  PricingSchemeWriteKeysForStepOne
> & {
  // We're abusing late_cancellation_fee_cents on the backend, but need to use
  // a different field name for react hook form to be happy and keep validations
  // separate for when the fee is flat vs percentage. This can fall off
  // once the backend is updated to use a separate field for percentage.
  late_cancellation_fee_percentage?: number;
  // Likewise, with no show fee
  no_show_fee_percentage?: number;
};

export type PricingWizardBaseConfigStepProps = {
  onCancel: () => void;
  onSubmit: (values: SchemeConfigFormValues) => void;
  workInProgressScheme: SchemeMetaFormValues;
  originalPricingSchemeValues?: PricingSchemeRead;
  mode: PricingWizardMode;
};

function FieldRowDivider() {
  return <div className="col-span-3 hidden border sm:block" />;
}

export function PricingWizardSchemeConfigurationStep({
  onCancel,
  onSubmit,
  workInProgressScheme,
  originalPricingSchemeValues,
  mode,
}: PricingWizardBaseConfigStepProps) {
  const formDefaultValues: SchemeConfigFormValues =
    assignConfigurationDefaultValues(originalPricingSchemeValues);
  const methods = useForm<SchemeConfigFormValues>({
    defaultValues: formDefaultValues,
  });
  const [formError, setFormError] = useState<string | null>(null);

  const { submitForm } = usePricingWizardFormSubmit({
    mode,
    workInProgressScheme,
    originalPricingSchemeValues,
    onSubmit,
    setFormError,
  });

  useScrollToFormErrors(formError ?? '', null);

  return (
    <div
      data-testid="pricing-wizard-scheme-config"
      className="mt-6 sm:max-w-7xl"
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(async (values) => {
            await submitForm(values);
          })}
          data-testid="pricing-wizard-scheme-config-form"
        >
          <div className="grid gap-2 md:grid-cols-3 md:gap-x-16 md:gap-y-3">
            {formError && (
              <div id="form-errors" className="col-span-3">
                <FormErrorMessage>{formError}</FormErrorMessage>
              </div>
            )}
            <div>
              <CurrencyFormContextField
                fieldPath="base_fee_cents"
                label="Base Fee"
                inline
                helperText="Enter the base fee that will be charged per trip."
              />
            </div>
            <div>
              <CurrencyFormContextField
                fieldPath="minimum_price_cents"
                label="Minimum Trip Price"
                inline
                helperText="Enter minimum price that will be charged per trip."
              />
            </div>
            <div>
              <MileageRoundingSelectField fieldPath="mileage_rounding" />
            </div>
            <div>
              <PriceRoundingSelectField
                fieldPath="price_rounding"
                rules={{
                  validate: (value) => {
                    const roundingInterval = methods.getValues(
                      'price_rounding_interval'
                    );
                    if (value && value !== 'none' && !roundingInterval) {
                      methods.setError('price_rounding_interval', {
                        message: 'Required when price rounding is enabled',
                      });
                    }
                    methods.clearErrors('price_rounding_interval');
                    return true;
                  },
                }}
              />
            </div>
            <div>
              <PriceIntervalSelectField
                fieldPath="price_rounding_interval"
                rules={{
                  validate: (value) => {
                    const roundingValue = methods.getValues('price_rounding');
                    if (roundingValue && roundingValue !== 'none' && !value) {
                      return 'Required when price rounding is enabled';
                    }
                    return true;
                  },
                }}
              />
            </div>
            <ConfigurationGridSpacer />

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="loaded_mileage_rate_cents"
                label="Loaded Mileage Rate"
                inline
                helperText="Enter the rate per mile for loaded miles."
              />
            </div>
            <div>
              <ContextFormFieldNumber
                fieldPath="free_loaded_miles"
                label="Free Miles"
                inline
                helperText="Enter the number of loaded miles that will be free of charge."
              />
            </div>
            <ConfigurationGridSpacer />

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="long_distance_mileage_rate_cents"
                label="Long Distance Mileage Rate"
                inline
                helperText="Enter the rate per mile for long distance miles."
              />
            </div>
            <div>
              <ContextFormFieldNumber
                fieldPath="long_distance_mileage_start"
                label="Long Distance Start"
                inline
                helperText="Specify the minimum number of miles required for the long distance rate to apply."
              />
            </div>
            <ConfigurationGridSpacer />

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="unloaded_mileage_rate_cents"
                label="Unloaded Pickup Mileage Rate"
                inline
                helperText="Enter the rate per mile for unloaded pickup miles (distance from HQ to pickup)."
              />
            </div>
            <div>
              <ContextFormFieldNumber
                fieldPath="free_unloaded_miles"
                label="Free Miles"
                inline
                helperText="Enter the number of unloaded pickup miles that will be free of charge."
              />
            </div>
            <ConfigurationGridSpacer />

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="unloaded_return_mileage_rate_cents"
                label="Unloaded Return Mileage Rate"
                inline
                helperText="Enter the rate per mile for unloaded return miles (distance from dropoff to HQ)."
              />
            </div>
            <div>
              <ContextFormFieldNumber
                fieldPath="free_unloaded_return_miles"
                label="Free Miles"
                inline
                helperText="Enter the number of unloaded return miles that will be free of charge."
              />
            </div>
            <ConfigurationGridSpacer />

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="wait_time_rate_cents"
                label="Wait Time Rate"
                inline
                helperText="Enter the rate per unit of waiting time."
              />
            </div>
            <div>
              <ContextFormFieldNumber
                fieldPath="wait_time_period_minutes"
                label="Time Period"
                inline
                helperText="Specify the period in minutes used to calculate the wait time rate. Ex: $20 per 15 minutes."
              />
            </div>
            <div>
              <ContextFormFieldNumber
                fieldPath="wait_time_free_minutes"
                label="Free Minutes"
                inline
                helperText="Enter the number of wait time minutes that will be free of charge."
              />
            </div>

            <FieldRowDivider />

            <div>
              <LateCancellationAmountField />
            </div>
            <div>
              <ContextFormFieldNumber
                fieldPath="late_cancellation_period_minutes"
                label="Time Window"
                inline
                helperText="Specify the number of minutes before the scheduled pickup time within which cancellations are considered late."
              />
            </div>
            <div>
              <LateCancellationFeeTypeField fieldPath="late_cancellation_fee_type" />
            </div>

            <FieldRowDivider />

            <div>
              <NoShowAmountField />
            </div>
            <div>
              <NoShowFeeTypeField fieldPath="no_show_fee_type" />
            </div>
            <ConfigurationGridSpacer />

            <FieldRowDivider />

            <AfterHoursPricingRow />

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="holiday_fee_cents"
                label="Holiday Fee"
                inline
                helperText="Enter the fee for trips that occur on holidays."
              />
            </div>
            <div>
              <ObservedHolidaysField fieldPath="observed_holidays" inline />
            </div>
            <div>
              <CurrencyFormContextField
                fieldPath="weekend_fee_cents"
                label="Weekend Fee"
                inline
                helperText="Enter the fee for trips that occur during weekends."
              />
            </div>

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="stair_fee_cents"
                label="Stair Fee"
                inline
                helperText="Enter the fee charged for trips involving stairs."
              />
            </div>
            <div>
              <ContextFormFieldNumber
                fieldPath="minimum_steps"
                label="Minimum Steps"
                inline
                helperText="Specify the minimum number of steps required for the stair fee to apply."
              />
            </div>
            <div>
              <StairFeeTypeField fieldPath="stair_fee_type" />
            </div>

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="wheelchair_rental_fee_cents"
                label="Wheelchair Rental"
                inline
                helperText="Enter the fee for providing a wheelchair."
              />
            </div>
            <div>
              <CurrencyFormContextField
                fieldPath="oxygen_fee_cents"
                label="Oxygen Fee"
                inline
                helperText="Enter the fee for providing oxygen during transport."
              />
            </div>
            <div>
              <CurrencyFormContextField
                fieldPath="additional_attendant_rate_cents"
                label="Additional Attendant"
                inline
                helperText="Enter the fee for each additional attendant required."
              />
            </div>

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="additional_passenger_rate_cents"
                label="Additional Passenger"
                inline
                helperText="Enter the fee for each additional passenger."
              />
            </div>
            <div>
              <CurrencyFormContextField
                fieldPath="bariatric_passenger_fee_cents"
                label="Bariatric Passenger"
                inline
                helperText="Enter the fee for transporting bariatric passengers."
              />
            </div>
            <div>
              <CurrencyFormContextField
                fieldPath="infectious_disease_fee_cents"
                label="Infectious Disease Fee"
                inline
                helperText="Enter the fee for transporting passengers with infectious diseases."
              />
            </div>

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="same_day_booking_fee_cents"
                label="Same Day Booking Fee"
                inline
                helperText="Enter the fee for same day bookings."
              />
            </div>
            <div>
              <CurrencyFormContextField
                fieldPath="car_seat_fee_cents"
                label="Car Seat Fee"
                inline
                helperText="Enter the fee for providing a car seat."
              />
            </div>
            <div>
              <CurrencyFormContextField
                fieldPath="booster_seat_fee_cents"
                label="Booster Seat Fee"
                inline
                helperText="Enter the fee for providing a booster seat."
              />
            </div>

            <FieldRowDivider />

            <div>
              <CurrencyFormContextField
                fieldPath="ride_alone_fee_cents"
                label="Ride Alone Fee"
                inline
                helperText="Enter the fee for providing a ride alone."
              />
            </div>
          </div>

          <div className="mt-4 flex justify-between gap-2">
            <Button
              onClick={onCancel}
              className="flex-grow"
              disabled={methods.formState.isSubmitting}
            >
              Cancel
            </Button>
            <FormSubmitButton className="flex-grow" buttonName="Save" />
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

/*
  Using these instead of colspan to keep label and inputs close and preserve alignment
*/
function ConfigurationGridSpacer() {
  return <div className="hidden md:block" />;
}
